/**
 * Installable app (PWA) on phones and computers.
 *  - Service worker (/sw.js, Cloudflare build only: <meta name="qh-sw">) registered once the map is
 *    up; a new version waits in the background → toast "Cập nhật" (reload when it took over).
 *  - Install: Chromium's beforeinstallprompt (Android, desktop) is kept for our own button (help
 *    dialog) and a one-time suggestion on phones from the 2nd visit; iOS / iPadOS have no prompt →
 *    the two taps are explained (Chia sẻ → Thêm vào MH chính).
 *  - Started from the home screen (display-mode standalone) → statistics entry "app".
 *  - Manifest shortcuts: /?go=search | saved | locate.
 *  - Offline / back-online notices (the service worker keeps what was already opened).
 */
import { toast, isMobile } from './ui.js';
import { act } from './telemetry.js';

const KEY = 'qh.pwa.v1';
const SUGGEST_AFTER = 25000;
const SUGGEST_AGAIN = 30 * 86400e3;
const UPDATE_CHECK = 30 * 60e3;

const st = (() => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } })();
const save = () => { try { localStorage.setItem(KEY, JSON.stringify(st)); } catch { /* private mode */ } };

let deferred = null; // BeforeInstallPromptEvent
let reg = null;
let updating = false;
let updateShown = false;
let wentOffline = false;
const listeners = new Set();
const notify = () => { for (const fn of listeners) { try { fn(installState()); } catch { /* ignore */ } } };

export const isStandalone = () => {
  try { return matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches || navigator.standalone === true; } catch { return false; }
};
export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
/** 'installed' (running as the app) | 'prompt' (one tap) | 'ios' (Share → Add to Home Screen) | 'manual' (browser menu). */
export const installState = () => (isStandalone() ? 'installed' : deferred ? 'prompt' : isIOS() ? 'ios' : 'manual');
export const onInstallChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// early: Chromium fires this soon after load, before the map is ready
if (typeof window !== 'undefined') {
  addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e; notify(); });
  addEventListener('appinstalled', () => {
    deferred = null;
    st.installed = Date.now();
    save();
    act('pwa', 'installed');
    toast('Đã cài Quy hoạch Hà Nội. Mở từ màn hình chính để dùng như một ứng dụng.', { type: 'ok', ms: 5000 });
    notify();
  });
}

const IOS_HELP = 'Trên iPhone, iPad: bấm nút Chia sẻ của Safari rồi chọn "Thêm vào MH chính".';
const MANUAL_HELP = 'Mở menu của trình duyệt rồi chọn "Cài đặt ứng dụng" hoặc "Thêm vào màn hình chính".';

/** The install action of our buttons. */
export async function promptInstall(from = 'button') {
  const s = installState();
  act('pwa', `${from}-${s}`);
  if (s === 'installed') { toast('Bạn đang dùng ứng dụng đã cài.', { type: 'ok' }); return 'installed'; }
  if (s === 'ios') { toast(IOS_HELP, { type: 'info', ms: 9000 }); return 'ios'; }
  if (s === 'manual') { toast(MANUAL_HELP, { type: 'info', ms: 8000 }); return 'manual'; }
  const e = deferred;
  deferred = null;
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    act('pwa', outcome); // accepted | dismissed
    if (outcome === 'dismissed') { st.dismissed = Date.now(); save(); }
    return outcome;
  } catch {
    return 'error';
  } finally {
    notify();
  }
}

/* ---------------- service worker ---------------- */

function showUpdate() {
  if (updateShown || updating) return;
  updateShown = true;
  toast('Đã có phiên bản mới của ứng dụng.', { type: 'info', ms: 20000, action: { label: 'Cập nhật', onClick: applyUpdate } });
}

function applyUpdate() {
  const w = reg?.waiting;
  act('pwa', 'update');
  if (!w) { location.reload(); return; }
  updating = true;
  w.postMessage({ type: 'skip-waiting' });
  setTimeout(() => location.reload(), 4000); // in case controllerchange never comes
}

function watch(r) {
  reg = r;
  if (r.waiting && navigator.serviceWorker.controller) showUpdate();
  r.addEventListener('updatefound', () => {
    const w = r.installing;
    w?.addEventListener('statechange', () => {
      if (w.state === 'installed' && navigator.serviceWorker.controller) showUpdate();
    });
  });
  let last = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || Date.now() - last < UPDATE_CHECK) return;
    last = Date.now();
    r.update().catch(() => {});
  });
}

function registerSW() {
  const url = document.querySelector('meta[name="qh-sw"]')?.content;
  if (!url || !('serviceWorker' in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => { if (updating) location.reload(); });
  const go = () => navigator.serviceWorker.register(url, { scope: '/', updateViaCache: 'none' }).then(watch).catch((e) => console.warn('service worker', e?.message || e));
  // after the start-up work: the precache download must not compete with the first tiles
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
  setTimeout(() => idle(go, { timeout: 3000 }), 2500);
}

/* ---------------- suggestion, shortcuts, offline ---------------- */

function maybeSuggest() {
  if (!isMobile() || isStandalone() || st.installed) return;
  if ((st.visits || 0) < 2 || (st.dismissed && Date.now() - st.dismissed < SUGGEST_AGAIN)) return;
  setTimeout(() => {
    const s = installState();
    if (s !== 'prompt' && s !== 'ios') return;
    if (document.querySelector('dialog[open]') || document.body.classList.contains('tool-active')) return;
    st.dismissed = Date.now(); // once a month at most, whatever the answer
    save();
    act('pwa', `suggest-${s}`);
    if (s === 'ios') toast(`Cài lên màn hình chính để mở nhanh hơn. ${IOS_HELP}`, { type: 'info', ms: 12000, action: { label: 'Đã hiểu' } });
    else toast('Cài Quy hoạch Hà Nội lên màn hình chính để mở nhanh hơn, dùng như một ứng dụng.', { type: 'info', ms: 12000, actions: [{ label: 'Cài đặt', onClick: () => promptInstall('suggest') }, { label: 'Để sau' }] });
  }, SUGGEST_AFTER);
}

/** ?go=… of the manifest shortcuts → action name (and the query removed from the address). */
export function takeShortcut() {
  const q = new URLSearchParams(location.search);
  const go = q.get('go');
  if (!go) return null;
  q.delete('go');
  const rest = q.toString();
  history.replaceState(history.state, '', location.pathname + (rest ? '?' + rest : '') + location.hash);
  return ['search', 'saved', 'locate'].includes(go) ? go : null;
}

/** After the map is ready. actions: { search, saved, locate } for the shortcuts. */
export function initPwa({ actions = {} } = {}) {
  st.visits = (st.visits || 0) + 1;
  save();
  const go = takeShortcut();
  if (go && actions[go]) { act('pwa', `shortcut-${go}`); setTimeout(() => actions[go](), 300); }
  registerSW();
  maybeSuggest();
  if (navigator.onLine === false) {
    wentOffline = true;
    setTimeout(() => toast('Đang không có mạng. Vẫn xem được bản đồ và những khu vực đã mở trước đây.', { type: 'warn', ms: 6000 }), 800);
  }
  addEventListener('offline', () => {
    wentOffline = true;
    toast('Mất kết nối mạng. Vẫn xem được bản đồ và những khu vực đã mở trước đây.', { type: 'warn', ms: 6000 });
  });
  addEventListener('online', () => {
    if (!wentOffline) return;
    wentOffline = false;
    toast('Đã có mạng trở lại.', { type: 'ok', ms: 2500 });
  });
  if (new URLSearchParams(location.search).has('debug')) window.__pwa = { installState, reg: () => reg, st };
}
